// pages/Clients/clients.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchfullClients,
  fetchOLEDDevices,
  selectOledDevices,
  fetchdealerfullClients,
  fetchofficefullClients,
  controlidfetchLogs,
  selectFullClients,
  selectClientsLoading,
  selectClientsError,
  selectTotalRecords,
  selectClientLogs,
  selectLogsLoading,
  clearError,
  clearLogs,
  addToRemovals
} from '../../features/clients/clientsSlice';
import InstallationForm from './installationform';
import { Search, X } from 'lucide-react';
import Swal from 'sweetalert2';
import ClientDetailsDrawer from './clientsdetailsdrawer';
import AddEditClient from './addeditclient';
import LogsModal from './logsmodal';
import ImageModal from './imagemodal';
import './clients.css';
import GPSModal from './gpsmodal';
import RoutePlay from './routeplay';
import MoreOptionsModal from './moreoptionsmodal';
import CalibrationHistoryModal from './calibrationhistorymodal';
import AddInvoiceModal from './addinvoicemodal';
import * as XLSX from "xlsx";
import JSZip from 'jszip';
import moment from 'moment-timezone';

const isImageEvent = log => ['PICTURE_CAPTURED', 'INITIAL_TEST', 'RANDOM_TEST'].includes(
  String(log?.event || log?.typeofevent || '').trim().toUpperCase().replace(/[\s-]+/g, '_')
);

const getClientField = (client, ...fieldNames) => {
  if (!client) return '';
  for (const fieldName of fieldNames) {
    const normalizedName = fieldName.toLowerCase().replace(/[^a-z]/g, '');
    const matchingKey = Object.keys(client).find(key => key.toLowerCase().replace(/[^a-z]/g, '') === normalizedName);
    if (matchingKey && client[matchingKey] != null && String(client[matchingKey]).trim()) return client[matchingKey];
  }
  return '';
};

const getTagState = client => String(getClientField(client, 'tagstate', 'tagState', 'tag_state') || '').trim().toUpperCase();

const dataUrlToBlob = dataUrl => {
  const [metadata, encoded] = dataUrl.split(',');
  const mimeType = metadata.match(/data:([^;]+)/)?.[1] || 'image/jpeg';
  const bytes = atob(encoded);
  const output = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) output[i] = bytes.charCodeAt(i);
  return new Blob([output], { type: mimeType });
};

const fetchEventImageBlob = async log => {
  const imageTime = log?.imgtime || log?.rawRtime || log?.time;
  const deviceId = log?.controlId;
  if (!imageTime || !deviceId) return null;

  const baseUrl = 'https://alcobrakeadmin.com/v3/app/getImage';
  for (const endpoint of ['getb64v4', 'getb64v42', 'getb64v44']) {
    try {
      const response = await fetch(`${baseUrl}/${endpoint}?deviceId=${encodeURIComponent(deviceId)}&Image=${encodeURIComponent(imageTime)}`);
      if (!response.ok) continue;
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('image')) return await response.blob();

      const text = (await response.text()).trim().replace(/^"|"$/g, '');
      if (!text || text.length <= 100 || /not found|error|null/i.test(text)) continue;
      if (text.startsWith('data:image')) return dataUrlToBlob(text);
      const mimeType = text.startsWith('iVBOR') ? 'image/png' : 'image/jpeg';
      return dataUrlToBlob(`data:${mimeType};base64,${text}`);
    } catch (error) {
      // Try the next legacy image endpoint.
    }
  }
  return null;
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const Clients = () => {
  const dispatch = useDispatch();
  const user = JSON.parse(localStorage.getItem('admin_user') || '{}');

  const fullClients = useSelector(selectFullClients);
  const oledDevices = useSelector(selectOledDevices);
  useEffect(() => { dispatch(fetchOLEDDevices()); }, [dispatch]);
  const loading = useSelector(selectClientsLoading);
  const error = useSelector(selectClientsError);
  const totalRecords = useSelector(selectTotalRecords);
  const clientLogs = useSelector(selectClientLogs);
  const logsLoading = useSelector(selectLogsLoading);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [goToPage, setGoToPage] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const [showMoreModal, setShowMoreModal] = useState(false);
  const [selectedMoreClient, setSelectedMoreClient] = useState(null);

  // Form modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  // Image modal state
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [currentImageLog, setCurrentImageLog] = useState(null);
  const [imageNotFound, setImageNotFound] = useState(false);

  const [showGPSModal, setShowGPSModal] = useState(false);
  const [gpsClient, setGpsClient] = useState(null);

  // Add to state
  const [showRoutePlay, setShowRoutePlay] = useState(false);
  const [routePlayClient, setRoutePlayClient] = useState(null);

  const [showInstallationFormModal, setShowInstallationFormModal] = useState(false);
  const [installationFormClientData, setInstallationFormClientData] = useState(null);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyClient, setHistoryClient] = useState(null);

  const [showAddInvoiceModal, setShowAddInvoiceModal] = useState(false);
  const [invoiceClient, setInvoiceClient] = useState(null);

  const [editClientId, setEditClientId] = useState(null);

  const handleHistory = (client) => {
    console.log('History for:', client);
    setHistoryClient(client);
    setShowHistoryModal(true);
  };
  
  const handleInstallationFormModal = (client) => {
    console.log('Opening Installation Form for:', client);
    setInstallationFormClientData(client);
    setShowInstallationFormModal(true);
  };

  const closeInstallationFormModal = () => {
    setShowInstallationFormModal(false);
    setInstallationFormClientData(null);
  };

  const handleRoutePlay = (client, e) => {
    if (e) e.stopPropagation();
    setRoutePlayClient(client);
    setShowRoutePlay(true);
  };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const refreshClients = useCallback(async () => {
      if (user.role === "Admin" || user.role === "superadmin") {
        await dispatch(fetchfullClients());
      } else if (String(user.role || '').toLowerCase() === 'office') {
        const officeId = user.officeId || user.officeid || user.office_id || user.id;
        if (officeId) await dispatch(fetchofficefullClients(officeId));
      } else {
        await dispatch(fetchdealerfullClients(user.dealerId));
      }
  }, [dispatch, user.role, user.dealerId, user.officeId, user.officeid, user.office_id, user.id]);

  useEffect(() => { refreshClients(); }, [refreshClients]);

  // Updated fetchClientLogs with sorted parameter
  const fetchClientLogs = useCallback(async (client, sorted = false) => {
    if (client && client.cbid && client.installDate) {
      return await dispatch(controlidfetchLogs({
        item: {
          id: client.id,
          cbid: client.cbid,
          installDate: client.installDate,
          sno: client.sno,
          devicemode: client.devicemode || client.deviceMode || client.devicemodel
        },
        role: user.role,
        sorted: sorted // true = unsorted (recently posted), false = sorted (by time)
      })).unwrap();
    }
    return [];
  }, [dispatch, user.role]);

  // New toggle sort handler
  const handleToggleSort = useCallback(async (isUnsorted) => {
    // isUnsorted = true → sorted: true → UNSORTED (recently posted)
    // isUnsorted = false → sorted: false → SORTED (by time)
    if (selectedClient) {
      await fetchClientLogs(selectedClient, isUnsorted);
    }
  }, [selectedClient, fetchClientLogs]);

  const fetchAndShowImage = async (log, eventId) => {
    if (!log || !log.controlId) {
      setSelectedImage(null);
      setCurrentImageLog(log || null);
      setImageNotFound(true);
      setImageLoading(false);
      setShowImageModal(true);
      return;
    }

    setImageLoading(true);
    setSelectedImage(null);
    setCurrentImageLog(log);
    setImageNotFound(false);

    try {
      const imageTime = log.imgtime || log.rawRtime || log.time;
      const deviceId = log.controlId;
      const eventIdQuery = eventId != null && eventId !== '' && eventId !== '-'
        ? `&eventid=${encodeURIComponent(eventId)}` : '';

      if (!imageTime) {
        setImageNotFound(true);
        setShowImageModal(true);
        return;
      }

      const endpoints = [
        `https://alcobrakeadmin.com/v3/app/getImage/getb64v4?deviceId=${deviceId}&Image=${encodeURIComponent(imageTime)}${eventIdQuery}`,
        `https://alcobrakeadmin.com/v3/app/getImage/getb64v42?deviceId=${deviceId}&Image=${encodeURIComponent(imageTime)}${eventIdQuery}`,
        `https://alcobrakeadmin.com/v3/app/getImage/getb64v44?deviceId=${deviceId}&Image=${encodeURIComponent(imageTime)}${eventIdQuery}`
      ];

      let imageData = null;

      for (const url of endpoints) {
        try {
          const response = await fetch(url);
          if (!response.ok) continue;

          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('image')) {
            imageData = URL.createObjectURL(await response.blob());
            break;
          }

          const text = (await response.text()).trim().replace(/^"|"$/g, '');

          if (text && text.length > 100 && !/not found|error|null/i.test(text)) {
            let imageSrc;
            if (text.startsWith('data:image')) {
              imageSrc = text;
            } else if (text.startsWith('/9j/')) {
              imageSrc = `data:image/jpeg;base64,${text}`;
            } else if (text.startsWith('iVBOR')) {
              imageSrc = `data:image/png;base64,${text}`;
            } else {
              imageSrc = `data:image/jpeg;base64,${text}`;
            }
            imageData = imageSrc;
            break;
          }
        } catch (err) {
          continue;
        }
      }

      if (imageData) {
        setSelectedImage(imageData);
        setShowImageModal(true);
      } else {
        setImageNotFound(true);
        setShowImageModal(true);
      }
    } catch (error) {
      setImageNotFound(true);
      setShowImageModal(true);
    } finally {
      setImageLoading(false);
    }
  };

  const handleGPSLocation = async (client, e) => {
    if (e) e.stopPropagation();
    setGpsClient(client);
    setShowGPSModal(true);
    await fetchClientLogs(client, false);
  };

  const handleCloseGPSModal = () => {
    setShowGPSModal(false);
    setGpsClient(null);
  };

  const handleRefreshGPS = () => {
    if (gpsClient) {
      fetchClientLogs(gpsClient, false);
    }
  };

  const handleMoreClick = (client, e) => {
    if (e) e.stopPropagation();
    setSelectedMoreClient(client);
    setShowMoreModal(true);
  };

  const handle920Form = (client) => {
    console.log('920 Form for:', client);
    Swal.fire({
      title: '920 Form',
      text: 'Feature coming soon',
      icon: 'info'
    });
  };

  const handleViewEditClient = (client) => {
    setEditingClient(client);
    setShowEditModal(true);
  };

  const handleCreateCustomer = async (client) => {
    const customerName = client?.client || client?.name || '';
    const email = client?.email || client?.clientemail || '';
    const phone = client?.clientphone1 || client?.phone || client?.primaryMobile || '';
    const customerForm = await Swal.fire({
      title: 'Create Customer',
      html: `
        <div class="create-customer-form">
          <label for="customer-name">Name</label>
          <input id="customer-name" class="swal2-input" type="text" autocomplete="name" />
          <label for="customer-email">Email</label>
          <input id="customer-email" class="swal2-input" type="email" autocomplete="email" />
          <label for="customer-phone">Phone</label>
          <input id="customer-phone" class="swal2-input" type="tel" autocomplete="tel" />
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: '#16a34a',
      confirmButtonText: 'Create Customer',
      focusConfirm: false,
      didOpen: () => {
        document.getElementById('customer-name').value = customerName;
        document.getElementById('customer-email').value = email;
        document.getElementById('customer-phone').value = phone;
      },
      preConfirm: () => {
        const nameValue = document.getElementById('customer-name').value.trim();
        const emailValue = document.getElementById('customer-email').value.trim();
        const phoneValue = document.getElementById('customer-phone').value.trim();
        if (!nameValue) {
          Swal.showValidationMessage('Name is required');
          return false;
        }
        if (emailValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
          Swal.showValidationMessage('Enter a valid email address');
          return false;
        }
        return { name: nameValue, email: emailValue, phone: phoneValue };
      }
    });
    if (!customerForm.isConfirmed) return;

    Swal.fire({ title: 'Creating customer…', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      const params = new URLSearchParams();
      params.append('email', customerForm.value.email);
      params.append('name', customerForm.value.name);
      params.append('phone', customerForm.value.phone);
      params.append('description', `${customerForm.value.name} ${client?.installDate || ''}`.trim());
      params.append('customerid', client?.id);
      params.append('type', 'interlock');

      const response = await fetch('https://alcobrakeadmin.com/v3/v2/stripepayments/addcustomer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params
      });
      if (!response.ok) throw new Error('Customer creation failed');
      const result = await response.json();
      if (result?.error || result?.success === false) throw new Error(result?.message || result?.error);

      await Swal.fire('Customer Created', 'The customer was created successfully.', 'success');
      if (user.role === 'Admin' || user.role === 'superadmin') await dispatch(fetchfullClients());
      else if (String(user.role || '').toLowerCase() === 'office') {
        const officeId = user.officeId || user.officeid || user.office_id || user.id;
        if (officeId) await dispatch(fetchofficefullClients(officeId));
      } else await dispatch(fetchdealerfullClients(user.dealerId));
    } catch (error) {
      Swal.fire('Error', error.message || 'Customer could not be created.', 'error');
    }
  };

  const handleAddInvoice = (client) => {
    setInvoiceClient(client);
    setShowAddInvoiceModal(true);
  };

  const handleAddToRemovals = async (client, e) => {
    if (e) e.stopPropagation();
    const fullClient = fullClients.find(item => String(item.id) === String(client.id));
    const removalClient = { ...client, ...(fullClient || {}) };
    const removalFormat = getTagState(removalClient) === 'TX' ? 'ZIP with CSV and event images' : 'CSV log file';

    const result = await Swal.fire({
      title: 'Move client to Removals',
      html: `
      <div class="removal-dialog-content">
        <div class="removal-client-summary">
          <strong>${removalClient.name || removalClient.client || 'Client'}</strong>
          <span>${removalClient.regNum || removalClient.regnum || 'No vehicle registration'}</span>
        </div>
        <div class="removal-log-notice">
          <span class="removal-log-icon">⇩</span>
          <div><strong>Logs will download first</strong><span>${removalFormat}</span></div>
        </div>
        <div class="removal-date-container">
        <label class="removal-date-label">Removal date</label>
        <input 
          type="date" 
          id="removalDate" 
          class="removal-date-input"
          value="${new Date().toISOString().split('T')[0]}" 
        >
        </div>
        <p class="removal-warning">The client will be moved only after the log download is prepared successfully.</p>
      </div>
    `,
      showCancelButton: true,
      confirmButtonText: 'Download logs & move',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      width: '450px',
      customClass: {
        popup: 'add-to-removals-modal',
        confirmButton: 'swal2-confirm-custom',
        cancelButton: 'swal2-cancel-custom'
      },
      preConfirm: () => {
        const inputEl = document.getElementById('removalDate');
        if (!inputEl || !inputEl.value) {
          Swal.showValidationMessage('Please select a valid date');
          return false;
        }
        const rawDate = inputEl.value;
        const parts = rawDate.split('-');
        const formattedDate = `${parts[1]}/${parts[2]}/${parts[0]}`;
        return formattedDate;
      }
    });

    if (result.isConfirmed && result.value) {
      Swal.fire({
        title: 'Downloading logs',
        html: '<div class="removal-progress-text">Preparing client event logs...</div>',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      try {
        const removalDate = result.value;
        const removalLogs = await fetchClientLogs(removalClient, false);
        await downloadLogsAsCSV(progress => {
          const detail = progress.total
            ? `${progress.completed} of ${progress.total} images processed · ${progress.included} included`
            : progress.status;
          Swal.update({
            title: progress.status === 'Creating ZIP file...' ? 'Creating ZIP' : 'Downloading logs',
            html: `<div class="removal-progress-text">${detail}</div>`
          });
        }, removalClient, removalLogs, getTagState(removalClient) === 'INDIA');

        Swal.update({
          title: 'Moving client',
          html: '<div class="removal-progress-text">Log download prepared. Moving client to Removals...</div>'
        });
        await dispatch(addToRemovals({
          item: removalClient,
          removalDate: removalDate
        })).unwrap();

        Swal.fire({
          title: 'Success!',
          text: 'Client successfully moved to removals',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
        return true;

      } catch (error) {
        await Swal.fire({
          title: 'Error',
          text: error || 'Failed to move to removals',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    }
    return false;
  };

  const handleExportClients = async () => {
    const result = await Swal.fire({
      title: 'Export Clients',
      text: 'Please choose the format',
      icon: 'info',
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: 'Excel (XLSX)',
      cancelButtonText: 'CSV',
      confirmButtonColor: '#2d6a4f',
      cancelButtonColor: '#6c757d'
    });

    if (result.isConfirmed) {
      exportToExcel();
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      exportToCSV();
    }
  };

  const exportToExcel = () => {
    const worksheetData = [
      [
        "ID", "Client Name", "Suffix", "Serial No", "Registration", "Phone", "Email",
        "Install Date", "Calibration Date", "Status", "DL Number", "Address", "City",
        "State", "Zipcode", "Make", "Model", "Year", "Color", "VIN", "Tag", "Tag State",
        "Prohibition Number", "Prohib Start", "Prohib End", "Conviction", "Calibration Period"
      ],
      ...fullClients.map(client => [
        client.id || '',
        client.client || '',
        client.suffix || '',
        client.sno || '',
        client.regnum || '',
        client.clientphone1 || '',
        client.email || '',
        client.installDate || '',
        client.calibrationDate || '',
        client.status || 'Active',
        client.DLnum || '',
        client.clientAddress || '',
        client.city || '',
        client.state || '',
        client.zipcode || '',
        client.make || '',
        client.model || '',
        client.year || '',
        client.color || '',
        client.vin || '',
        client.tag || '',
        client.tagstate || '',
        client.prohibitionnumber || '',
        client.prohibstart || '',
        client.prohibend || '',
        client.conviction || '',
        client.calibrationperiod || ''
      ])
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Clients");
    XLSX.writeFile(workbook, `clients_${new Date().toISOString().split('T')[0]}.xlsx`);

    Swal.fire({
      title: 'Export Complete!',
      text: 'Excel file has been downloaded',
      icon: 'success',
      timer: 2000,
      showConfirmButton: false
    });
  };

  const exportToCSV = () => {
    const headers = [
      "ID", "Client Name", "Suffix", "Serial No", "Registration", "Phone", "Email",
      "Install Date", "Calibration Date", "Status", "DL Number", "Address", "City",
      "State", "Zipcode", "Make", "Model", "Year", "Color", "VIN", "Tag", "Tag State"
    ];

    const csvRows = [headers];

    fullClients.forEach(client => {
      csvRows.push([
        client.id || '',
        client.client || '',
        client.suffix || '',
        client.sno || '',
        client.regnum || '',
        client.clientphone1 || '',
        client.email || '',
        client.installDate || '',
        client.calibrationDate || '',
        client.status || 'Active',
        client.DLnum || '',
        client.clientAddress || '',
        client.city || '',
        client.state || '',
        client.zipcode || '',
        client.make || '',
        client.model || '',
        client.year || '',
        client.color || '',
        client.vin || '',
        client.tag || '',
        client.tagstate || ''
      ]);
    });

    const csvContent = csvRows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clients_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    Swal.fire({
      title: 'Export Complete!',
      text: 'CSV file has been downloaded',
      icon: 'success',
      timer: 2000,
      showConfirmButton: false
    });
  };

  const transformedClients = useMemo(() => {
    if (!fullClients || !Array.isArray(fullClients)) return [];
    return fullClients.map(client => ({
      id: client.id,
      sno: client.sno || '',
      name: String(client.client || '').replace(/^\d+(?:india|ca)v\d+$/i, value => value.toUpperCase()),
      regNum: client.regnum || '',
      phone: client.clientphone1 || '',
      email: client.email || '',
      dob: client.dob || '',
      installDate: client.installDate || '',
      calibrationDate: client.calibrationDate || '',
      lastActive: client.lastupdate || '—',
      status: client.status || 'Active',
      cbid: client.cbid,
      devId: client.devId,
      devicemode: client.devicemode || client.deviceMode || client.devicemodel || '',
      deviceMode: client.deviceMode || client.devicemode || client.devicemodel || '',
      fwversion: client.fwversion || 'N/A',
      customer_id: client.customer_id || null,
      instate: client.instate || client.inState || client.in_state || '',
      tagstate: client.tagstate || client.tagState || client.tag_state || '',
      state: client.state || '',
      rstate: client.rstate || '',
    }));
  }, [fullClients]);

  const filteredClients = useMemo(() => {
    if (!searchTerm) return transformedClients;
    const searchLower = searchTerm.toLowerCase();
    return transformedClients.filter(client =>
      client.name?.toLowerCase().includes(searchLower) ||
      client.sno?.toLowerCase().includes(searchLower) ||
      client.regNum?.toLowerCase().includes(searchLower) ||
      client.phone?.includes(searchLower)
    );
  }, [transformedClients, searchTerm]);

  // ============ PAGINATION - FIXED ============
  const totalItems = filteredClients.length;
  
  // Calculate total pages
  const getTotalPages = useCallback(() => {
    if (rowsPerPage === 'all' || rowsPerPage >= totalItems) {
      return 1;
    }
    return Math.max(1, Math.ceil(totalItems / rowsPerPage));
  }, [totalItems, rowsPerPage]);

  const totalPages = getTotalPages();

  // Reset to page 1 when search or rows per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, rowsPerPage]);

  // Ensure current page is valid
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
    if (currentPage < 1) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Get current page items
  const getCurrentItems = useCallback(() => {
    if (rowsPerPage === 'all' || rowsPerPage >= totalItems) {
      return filteredClients;
    }
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, totalItems);
    return filteredClients.slice(startIndex, endIndex);
  }, [filteredClients, currentPage, rowsPerPage, totalItems]);

  const currentItems = getCurrentItems();

  // Calculate displayed range
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endItem = Math.min(currentPage * rowsPerPage, totalItems);

  // Pagination handlers
  const handlePageChange = useCallback((page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setGoToPage('');
      // Scroll to top of table
      const tableElement = document.querySelector('.table-wrapper-simple');
      if (tableElement) {
        tableElement.scrollTop = 0;
      }
    }
  }, [totalPages]);

  const handleRowsPerPageChange = useCallback((e) => {
    const value = e.target.value;
    if (value === 'all') {
      setRowsPerPage(totalItems);
    } else {
      setRowsPerPage(Number(value));
    }
    setCurrentPage(1);
    setGoToPage('');
  }, [totalItems]);

  const handleGoToPage = useCallback((e) => {
    e.preventDefault();
    const pageNum = parseInt(goToPage);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
      setGoToPage('');
    }
  }, [goToPage, totalPages]);

  // Generate page numbers for display - FIXED
  const getPageNumbers = useCallback(() => {
    const pages = [];
    const maxVisible = 5;
    const halfVisible = Math.floor(maxVisible / 2);

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - halfVisible);
      let endPage = Math.min(totalPages, currentPage + halfVisible);

      // Adjust if we're at the beginning
      if (currentPage - halfVisible < 1) {
        endPage = Math.min(totalPages, endPage + (halfVisible - currentPage + 1));
      }
      
      // Adjust if we're at the end
      if (currentPage + halfVisible > totalPages) {
        startPage = Math.max(1, startPage - (currentPage + halfVisible - totalPages));
      }

      // Add first page and ellipsis if needed
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add last page and ellipsis if needed
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        pages.push(totalPages);
      }
    }

    return pages;
  }, [currentPage, totalPages]);

  const handleViewClient = (client) => {
    setSelectedClient(client);
    setShowDrawer(true);
  };

  const handleEditClient = (client, e) => {
    if (e) e.stopPropagation();
    const fullClientData = fullClients.find(c => c.id === client.id);
    setEditingClient(fullClientData);
    setShowEditModal(true);
  };

  const handleAddClient = () => {
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingClient(null);
  };

  const handleSaveClient = () => {
    if (user.role === "Admin" || user.role === "superadmin") {
      dispatch(fetchfullClients());
    } else {
      dispatch(fetchdealerfullClients(user.dealerId));
    }
  };

  // Updated handleViewLogs with sorted parameter
  const handleViewLogs = async (client, e) => {
    if (e) e.stopPropagation();
    const fullClient = fullClients.find(item => String(item.id) === String(client.id));
    const clientWithStates = { ...client, ...(fullClient || {}) };
    setSelectedClient(clientWithStates);
    setShowLogsModal(true);
    await fetchClientLogs(clientWithStates, false); // Default: sorted (false = sorted by time)
  };

  const handleCloseDrawer = () => {
    setShowDrawer(false);
    setSelectedClient(null);
  };

  const handleCloseLogsModal = () => {
    setShowLogsModal(false);
    setSelectedClient(null);
    dispatch(clearLogs());
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
    setCurrentImageLog(null);
    setImageNotFound(false);
  };

  const downloadLogsAsCSV = async (onProgress, exportClient = selectedClient, logsToExport = clientLogs, csvOnly = false) => {
    const exportLogs = Array.isArray(logsToExport) ? logsToExport : [];

    const headers = [
      'Sampler ID', 'Control Board ID', 'Time', 'Event',
      'BAC', 'Result'
    ];

    const csvRows = [headers];

    exportLogs.forEach(log => {
      csvRows.push([
        log.samplerId || '',
        log.controlId || '',
        log.time || '',
        log.event || '',
        /^OTP(?:_|\b)/i.test(log.event || log.typeofevent || '') || ['41', '42', '43', '44'].includes(String(log.rawEventType)) ? '' : log.bac || '',
        log.result || ''
      ]);
    });

    const escapeCsv = value => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csvContent = `\uFEFF${csvRows.map(row => row.map(escapeCsv).join(',')).join('\r\n')}`;
    const safeName = String(exportClient?.name || exportClient?.client || 'logs').replace(/[<>:"/\\|?*]+/g, '_');
    const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });

    if (csvOnly || !['TX', 'INDIA'].includes(getTagState(exportClient))) {
      downloadBlob(csvBlob, `${safeName}_logs.csv`);
      return;
    }

    try {
      const zip = new JSZip();
      zip.file(`${safeName}_logs.csv`, csvBlob);
      const imageLogs = exportLogs.filter(isImageEvent);
      const availableImages = [];
      onProgress?.({ completed: 0, total: imageLogs.length, included: 0, status: 'Downloading images...' });
      for (let index = 0; index < imageLogs.length; index += 1) {
        const log = imageLogs[index];
        const blob = await fetchEventImageBlob(log);
        if (blob) {
          const extension = blob.type.includes('png') ? 'png' : 'jpg';
          const storedTime = log.imgtime || log.rawRtime || log.time;
          const imageMoment = moment.utc(storedTime, ['MM/DD/YYYY HH:mm:ss', moment.ISO_8601], true);
          const imageTime = imageMoment.isValid()
            ? imageMoment.tz(getTagState(exportClient) === 'INDIA' ? 'Asia/Kolkata' : 'America/Chicago').format('MM_DD_YYYY_HH_mm_ss')
            : String(storedTime || index + 1).replace(/[^a-z0-9_-]+/gi, '_');
          const imageName = `images/${imageTime}.${extension}`;
          const uniqueName = availableImages.some(image => image.name === imageName)
            ? `images/${imageTime}_${index + 1}.${extension}`
            : imageName;
          availableImages.push({ name: uniqueName, blob });
        }
        onProgress?.({ completed: index + 1, total: imageLogs.length, included: availableImages.length, status: 'Downloading images...' });
      }
      onProgress?.({ completed: imageLogs.length, total: imageLogs.length, included: availableImages.length, status: 'Creating ZIP file...' });
      availableImages.forEach(image => zip.file(image.name, image.blob));
      const installDate = String(exportClient?.installDate || 'no_install_date').replace(/[^a-z0-9_-]+/gi, '-');
      downloadBlob(await zip.generateAsync({ type: 'blob' }), `${safeName}_${installDate}.zip`);
      return availableImages.length;
    } catch (error) {
      throw new Error('The CSV or event images could not be packaged.');
    }
  };

  // Updated refreshLogs to maintain current sort state
  const refreshLogs = useCallback((isUnsorted = false) => {
  if (selectedClient) {
    fetchClientLogs(selectedClient, isUnsorted);
  }
}, [selectedClient, fetchClientLogs]);

  const getInitials = (name) => {
    if (!name) return '??';
    const words = name.trim().split(/\s+/);
    return `${words[0]?.[0] || ''}${words.length > 1 ? words[words.length - 1][0] : ''}`.toUpperCase();
  };

  const avatarColors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444', '#06b6d4'];
  const getAvatarColor = (name) => avatarColors[name?.charCodeAt(0) % avatarColors.length] || '#3b82f6';

  const renderSkeleton = () => {
  const skeletonRows = Array.from({ length: 8 });
 
  return (
    <div className="app-container">
      <div className="main-content">
 
        {/* Header Skeleton */}
        <div className="header-simple">
          <div>
            <div className="skeleton skeleton-title"></div>
            <div className="skeleton skeleton-subtitle"></div>
          </div>
 
          <div className="header-stats">
            <div className="skeleton skeleton-stat"></div>
            <div className="skeleton skeleton-button"></div>
            <div className="skeleton skeleton-button"></div>
          </div>
        </div>
 
        {/* Search Skeleton */}
        <div className="search-section-simple">
          <div className="skeleton skeleton-search"></div>
          <div className="skeleton skeleton-select"></div>
        </div>
 
        {/* Table Skeleton */}
        <div className="table-wrapper-simple">
          <table className="client-table-simple">
            <thead>
              <tr>
                <th>Client</th>
                <th>Serial No.</th>
                <th>Registration</th>
                <th>Phone</th>
                <th>Install Date</th>
                <th>DOB</th>
                <th>Status</th>
                <th colSpan="5">Actions</th>
              </tr>
            </thead>
 
            <tbody>
              {skeletonRows.map((_, index) => (
                <tr key={index}>
                  <td>
                    <div className="skeleton-client">
                      <div className="skeleton skeleton-avatar"></div>
                      <div className="skeleton skeleton-name"></div>
                    </div>
                  </td>
 
                  <td><div className="skeleton skeleton-text"></div></td>
                  <td><div className="skeleton skeleton-text"></div></td>
                  <td><div className="skeleton skeleton-text"></div></td>
                  <td><div className="skeleton skeleton-text"></div></td>
                  <td><div className="skeleton skeleton-text"></div></td>
                  <td><div className="skeleton skeleton-status"></div></td>
 
                  {[1, 2, 3, 4, 5].map(item => (
                    <td key={item}>
                      <div className="skeleton skeleton-action"></div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
 
      </div>
    </div>
  );
};
 
  
  if (loading && fullClients.length === 0) {
    return renderSkeleton();
    
  }

  return (
    <div className="app-container">
      <div className="main-content">
        {/* Header */}
        <div className="header-simple">
          <div>
            <h1 className="title">Client Management</h1>
            <p className="subtitle">Manage and monitor all clients</p>
          </div>
          <div className="header-stats">
            <div className="stat-chip">
              <span className="stat-num">{searchTerm ? filteredClients .length :totalRecords}</span>
              <span className="stat-label">Total Clients</span>
            </div>
            <button className="btn-add" onClick={handleAddClient}>
              <span>+</span> Add Client
            </button>
            <button className="btn-export" onClick={handleExportClients}>
              📊 Export
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-section-simple">
          <div className="search-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search ..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
            {searchTerm && (
              <button
                className="clear-search"
                onClick={() => {
                  setSearchTerm('');
                  setCurrentPage(1);
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>
                          <div className='per-page-select-wrapper'>
          <select
            className="per-page-select"
            value={rowsPerPage === totalItems ? 'all' : rowsPerPage}
            onChange={handleRowsPerPageChange}
          >
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
            <option value={500}>500 per page</option>
            <option value="all">All</option>
          </select>
   <span className="per-page-arrow"></span>
          </div>
        </div>

        {error && <div className="error-banner"><span>{error}</span><button onClick={() => dispatch(clearError())}>✕</button></div>}

        {filteredClients.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>No clients found</h3>
            <p>Try adjusting your search</p>
          </div>
        ) : isMobile ? (
          <div className="cards-container">
            {currentItems.map((client) => (
              <div key={client.id} className="card-simple" onClick={() => handleViewClient(client)}>
                <div className="card-header-simple">
                  <div className="card-avatar" style={{ background: getAvatarColor(client.name) }}>
                    {getInitials(client.name)}
                  </div>
                  <div className="card-info">
                    <div className="card-name">{client.name}</div>
                    <div className="card-sno">{client.sno}</div>
                  </div>
                  <div className={`status-badge ${client.status?.toLowerCase() || 'active'}`}>{client.status || 'Active'}</div>
                </div>
                <div className="card-details">
                  <div className="detail-item"><span>📝</span> {client.regNum}</div>
                  <div className="detail-item"><span>📞</span> {client.phone}</div>
                  <div className="detail-item"><span>📅</span> {client.installDate || '—'}</div>
                </div>
               <div className="card-actions-simple">
 
              <button
                className="action-btn-icon edit-btn"
                title="Edit"
                onClick={(e) => handleEditClient(client, e)}
              >
                <span className="action-icon">✏️</span>
                <span className="action-label">Edit</span>
              </button>
            
              <button
                className="action-btn-icon"
                title="GPS Location"
                onClick={(e) => handleGPSLocation(client, e)}
              >
                <span className="action-icon">📍</span>
                <span className="action-label">GPS</span>
              </button>
            
              <button
                className="action-btn-icon logs-btn"
                title="Logs"
                onClick={(e) => handleViewLogs(client, e)}
              >
                <span className="action-icon">📋</span>
                <span className="action-label">Logs</span>
              </button>
            
              <button
                className="action-btn-icon more-btn"
                title="More"
                onClick={(e) => handleMoreClick(client, e)}
              >
                <span className="action-icon">⋯</span>
                <span className="action-label">More</span>
              </button>
            
              <button
                className="action-btn-icon delete-btn"
                title="Add to Removals"
                onClick={(e) => handleAddToRemovals(client, e)}
              >
                <span className="action-icon">🗑️</span>
                <span className="action-label">Delete</span>
              </button>
            
            </div>
            
              </div>
            ))}
            <div className="mobile-pagination">
              <button 
                className={`mobile-page-btn ${currentPage === 1 ? 'disabled' : ''}`}
                onClick={() => handlePageChange(currentPage - 1)} 
                disabled={currentPage === 1}
              >
                ←
              </button>
              <span className="mobile-page-info">{currentPage} / {totalPages}</span>
              <button 
                className={`mobile-page-btn ${currentPage === totalPages ? 'disabled' : ''}`}
                onClick={() => handlePageChange(currentPage + 1)} 
                disabled={currentPage === totalPages}
              >
                →
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="table-wrapper-simple">
              <table className="client-table-simple">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Serial No.</th>
                    <th>Registration</th>
                    <th>Phone</th>
                    <th>Install Date</th>
                    <th>DOB</th>
                    <th className="text-center">Status</th>
                    <th colSpan="5">Actions</th>
                  </tr>
                  <tr className="action-headers-row">
                    <th></th>
                    <th></th>
                    <th></th>
                    <th></th>
                    <th></th>
                    <th></th>
                    <th></th>
                    <th className="action-header-cell text-center">Edit/View</th>
                    <th className="action-header-cell text-center">GPS</th>
                    <th className="action-header-cell text-center">Logs</th>
                    <th className="action-header-cell text-center">More</th>
                    <th className="action-header-cell text-center">Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((client) => (
                    <tr key={client.id} className="client-row" onClick={() => handleViewClient(client)}>
                      <td className="client-cell-td">
                        <div className="client-cell">
                          <div className="avatar-small" style={{ background: getAvatarColor(client.name) }}>
                            {getInitials(client.name)}
                          </div>
                          <div className="client-info">
                            <div className="client-name">{client.name}</div>
                          </div>
                        </div>
                      </td>
                      <td><span className="sno-badge">{client.sno}</span></td>
                      <td><span className="reg-badge">{client.regNum}</span></td>
                      <td>{client.phone}</td>
                      <td>{client.installDate || '—'}</td>
                      <td>{client.dob || '—'}</td>
                      <td><span className={`status-badge ${client.status?.toLowerCase() || 'active'}`}>{client.status || 'Active'}</span></td>
                      <td className="action-cell text-center">
                        <button className="icon-btn edit-btn" title="Edit Client" onClick={(e) => handleEditClient(client, e)}>
                          <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-9.5 9.5L3 16l.086-2.914 9.5-9.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M11 4l3 3" stroke="currentColor" strokeWidth="1.5" />
                          </svg>
                        </button>
                      </td>
                      <td className="action-cell text-center">
                        <button className="icon-btn gps-btn" title="GPS Location" onClick={(e) => handleGPSLocation(client, e)}>
                          <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
                            <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="1.8" />
                            <path d="M10 2v2M10 16v2M2 10h2M16 10h2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                          </svg>
                        </button>
                      </td>
                      <td className="action-cell text-center">
                        <button className="icon-btn logs-btn" title="View Logs" onClick={(e) => handleViewLogs(client, e)}>
                          <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
                            <rect x="2" y="2" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
                            <path d="M6 6h8M6 10h6M6 14h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                        </button>
                      </td>
                      <td className="action-cell text-center">
                        <button
                          className="icon-btn more-btn"
                          title="More Options"
                          onClick={(e) => handleMoreClick(client, e)}
                        >
                          <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
                            <circle cx="10" cy="5" r="1.5" stroke="currentColor" strokeWidth="1.5" />
                            <circle cx="10" cy="10" r="1.5" stroke="currentColor" strokeWidth="1.5" />
                            <circle cx="10" cy="15" r="1.5" stroke="currentColor" strokeWidth="1.5" />
                          </svg>
                        </button>
                      </td>
                      <td className="action-cell text-center">
                        <button
                          className="icon-btn delete-btn"
                          title="Add to Removals"
                          onClick={(e) => handleAddToRemovals(client, e)}
                        >
                          <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
                            <path d="M4 6h12M8 6V4h4v2M6 10v6M10 10v6M14 10v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* PAGINATION - FIXED with active class */}
            <div className="pagination-simple">
              <div className="pagination-info">
                Showing <strong>{startItem}–{endItem}</strong> of <strong>{totalItems}</strong>
              </div>
              <div className="pagination-controls">
                <button 
                  className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`}
                  onClick={() => handlePageChange(1)} 
                  disabled={currentPage === 1 || totalPages === 1}
                >
                  «
                </button>
                <button 
                  className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`}
                  onClick={() => handlePageChange(currentPage - 1)} 
                  disabled={currentPage === 1 || totalPages === 1}
                >
                  ‹
                </button>
                {getPageNumbers().map((p, i) => {
                  if (p === '...') {
                    return <span key={`ellipsis-${i}`} className="page-dots">…</span>;
                  }
                  return (
                    <button 
                      key={`page-${p}`} 
                      className={`page-btn ${currentPage === p ? 'active' : ''}`}
                      onClick={() => handlePageChange(p)}
                    >
                      {p}
                    </button>
                  );
                })}
                <button 
                  className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`}
                  onClick={() => handlePageChange(currentPage + 1)} 
                  disabled={currentPage === totalPages || totalPages === 1}
                >
                  ›
                </button>
                <button 
                  className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`}
                  onClick={() => handlePageChange(totalPages)} 
                  disabled={currentPage === totalPages || totalPages === 1}
                >
                  »
                </button>
              </div>
              <div className="goto-wrapper">
                <form onSubmit={handleGoToPage}>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={goToPage}
                    onChange={(e) => setGoToPage(e.target.value)}
                    placeholder="Go to"
                    disabled={totalPages === 1}
                  />
                  <button type="submit" disabled={totalPages === 1}>Go</button>
                </form>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Installation Form Modal */}
      {showInstallationFormModal && installationFormClientData && (
        <div className="installation-modal-wrapper">
          <InstallationForm
            clientId={installationFormClientData}
            onSuccess={() => {
              console.log('Installation success');
              closeInstallationFormModal();
            }}
            onClose={closeInstallationFormModal}
          />
        </div>
      )}

      {/* Client Details Drawer */}
      <ClientDetailsDrawer
        isOpen={showDrawer}
        onClose={handleCloseDrawer}
        client={selectedClient}
        user={user}
        oledDevices={oledDevices}
        onAddToRemovals={handleAddToRemovals}
        onSwapSuccess={refreshClients}
      />

      {/* Add Client Modal */}
      <AddEditClient
        isOpen={showAddModal}
        onClose={handleCloseAddModal}
        onSave={handleSaveClient}
        title="Add Client"
        user={user}
      />

      {/* Edit Client Modal */}
      <AddEditClient
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingClient(null);
        }}
        onSave={handleSaveClient}
        clientData={editingClient}
        title="Edit Client"
        user={user}
      />

      {/* Logs Modal - Updated with toggle props */}
      <LogsModal
        isOpen={showLogsModal}
        onClose={handleCloseLogsModal}
        client={selectedClient}
        logs={clientLogs}
        logsLoading={logsLoading}
        onRefresh={refreshLogs}
        onDownload={downloadLogsAsCSV}
        onViewImage={fetchAndShowImage}
        onToggleSort={handleToggleSort}
        user={user}
      />

      {/* Image Modal */}
      <ImageModal
        isOpen={showImageModal}
        onClose={closeImageModal}
        imageData={selectedImage}
        imageLoading={imageLoading}
        imageNotFound={imageNotFound}
        currentLog={currentImageLog}
      />

      <RoutePlay
        isOpen={showRoutePlay}
        onClose={() => {
          setShowRoutePlay(false);
          setRoutePlayClient(null);
        }}
        client={routePlayClient}
      />

      <GPSModal
        isOpen={showGPSModal}
        onClose={handleCloseGPSModal}
        client={gpsClient}
        user={user}
      />

      <MoreOptionsModal
        isOpen={showMoreModal}
        onClose={() => {
          setShowMoreModal(false);
          setSelectedMoreClient(null);
        }}
        client={selectedMoreClient}
        user={user}
        onHistory={handleHistory}
        on920Form={handle920Form}
        onInstallationForm={handleInstallationFormModal}
        onViewEdit={handleViewEditClient}
        onCreateCustomer={handleCreateCustomer}
        onAddInvoice={handleAddInvoice}
      />

      <CalibrationHistoryModal
        isOpen={showHistoryModal}
        onClose={() => {
          setShowHistoryModal(false);
          setHistoryClient(null);
        }}
        client={historyClient}
      />

      <AddInvoiceModal
        isOpen={showAddInvoiceModal}
        onClose={() => {
          setShowAddInvoiceModal(false);
          setInvoiceClient(null);
        }}
        client={invoiceClient}
      />
    </div>
  );
};

export default Clients;
